import * as React from 'react';
import cx from 'classnames';

import { TagList } from '../TagList/TagList';
import { Clickable } from '../Clickable/Clickable';
import { EntityCard, type EntityProps } from '../EntityCard/EntityCard';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';
import { getClientTags } from '../../utils/client';
import { routes } from '../../config/routes';

import * as cs from './ClientCard.module.css';

interface Props extends EntityProps {
  industries?: CustomTypes.FilterTag[];
  services?: CustomTypes.FilterTag[];
  skills?: CustomTypes.FilterTag[];
  size: 'sm' | 'md' | 'lg' | 'lg_condenced';
  childClients?: {
    slug: string;
    title: string;
    logo: {
      publicURL: string;
    };
  }[];
  withEntityType?: boolean;
  facts?: CustomTypes.Facts | null;
  logo?: {
    publicURL: string;
  };
  coverImage?: Queries.BlogPostPageEntryFragment['coverImage'];
  bgImage?: Queries.BlogPostPageEntryFragment['bgImage'];
  bgColorOverflow?: boolean | null;
  details?: React.ReactNode;
  hideTitle?: boolean;
}

export const ClientCard = ({
  description,
  slug,
  industries,
  services,
  childClients,
  skills,
  withEntityType,
  size,
  hideTitle = true,
  ...restProps
}: Props) => {
  const details = (
    <TagList
      entries={getClientTags({
        industries,
        services,
        skills,
        withEntityType,
      })}
      mainVariant={withEntityType ? 'EntityType' : 'Industry'}
    />
  );

  const content = (
    <>
      <div className={cx(cs.description, cs[`size_${size}`])}>
        {description}
      </div>
      {childClients && childClients.length > 0 && (
        <div className={cs.childClientsWrapper}>
          <div className={cs.childClients}>
            {childClients.map((childClient) => (
              <Clickable
                key={childClient.slug}
                className={cs.childLogoWrapper}
                to={routes.client(childClient.slug)}
                aria-label={childClient.title}
              >
                <VisuallyHidden>{childClient.title}</VisuallyHidden>

                <img
                  src={childClient.logo && childClient.logo.publicURL}
                  alt={`${childClient.title} logo`}
                  className={cs.childLogo}
                  loading="lazy"
                />
              </Clickable>
            ))}
          </div>
        </div>
      )}
    </>
  );

  return (
    <EntityCard
      type="client"
      slug={slug}
      url={
        childClients && childClients.length > 0
          ? routes.client(childClients[0].slug)
          : routes.client(slug)
      }
      details={details}
      description={content}
      hideTitle={hideTitle}
      logoClassName={cs.logo}
      size={size}
      {...restProps}
    />
  );
};
